/**
 * Collapse the stream of `image_generated` activities into one entry per job.
 *
 * The backend emits several events per job (0 % placeholder, partial frames,
 * final) and, on durable turns, also mirrors a storage-safe copy of each event
 * with `image_url` stripped (Cosmos size). That stripped copy can arrive after
 * the live final frame, so "last wins" alone would replace a rendered image
 * with an empty "not available in history" card (#3043). A later entry for the
 * same job therefore inherits `image_url` (and `session_file`) from the entry
 * it replaces whenever it lacks them itself.
 */

import type { ActivityMessage } from "@/api/models"

import { BACKEND_URI } from "@/constants/config"
import { imageFileExtension, isImageFileExtension } from "@/features/chat/utils/image-file-mime"

const CARRIED_KEYS = ["image_url", "session_file"] as const

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0
}

function jobIdOf(activity: ActivityMessage): string | undefined {
  const jobId = activity.details?.job_id
  return typeof jobId === "string" ? jobId : undefined
}

function mergeReplacement(previous: ActivityMessage, next: ActivityMessage): ActivityMessage {
  const previousDetails = previous.details ?? {}
  const nextDetails = next.details ?? {}
  const carried: Record<string, unknown> = {}
  for (const key of CARRIED_KEYS) {
    if (!nonEmptyString(nextDetails[key]) && nonEmptyString(previousDetails[key])) {
      carried[key] = previousDetails[key]
    }
  }
  // A durable turn also mirrors the storage-safe copy of each frame, which can
  // arrive after the live final: a later partial never un-finalises an entry.
  if (!previousDetails.partial && Boolean(nextDetails.partial)) {
    carried.partial = false
  }
  if (Object.keys(carried).length === 0) return next
  return { ...next, details: { ...nextDetails, ...carried } }
}

/**
 * The frame's pixels are done: the final entry, or a partial at 100 % (the
 * image is generated and billed; the backend is still storing the file).
 * One rule for the dedupe filter, the localStorage image cache and the card.
 */
export function isImageFinishedFrame(details: object | undefined): boolean {
  if (!details) return false
  const { partial, progress } = details as { partial?: unknown; progress?: unknown }
  if (!partial) return true
  return typeof progress === "number" && progress >= 100
}

/**
 * URL of a file the assistant saved into the session: images, decks, anything
 * served by session-content. One form for every consumer (the inline answer
 * image uses the same base), so one file is one React Query entry.
 */
export function sessionFileContentUrl(sessionId: string, filename: string): string {
  return `${BACKEND_URI}/session-content/${sessionId}/${encodeURIComponent(filename)}`
}

export function dedupeImageActivities(
  activities: ActivityMessage[],
  isActivelyStreaming: boolean
): ActivityMessage[] {
  const indexByJobId = new Map<string, number>()
  const ordered: ActivityMessage[] = []

  for (const activity of activities) {
    if (activity.activity_type !== "image_generated") continue
    const jobId = jobIdOf(activity)
    if (!jobId) {
      ordered.push(activity)
      continue
    }
    const existingIdx = indexByJobId.get(jobId)
    if (existingIdx === undefined) {
      indexByJobId.set(jobId, ordered.length)
      ordered.push(activity)
    } else {
      ordered[existingIdx] = mergeReplacement(ordered[existingIdx], activity)
    }
  }

  if (isActivelyStreaming) return ordered

  // Once the turn finished, a slot still marked partial without any image is a
  // stream that got cancelled before its final frame: drop it rather than
  // leave an empty "Generating…" box behind. A partial at 100% is different:
  // the image was generated (and billed) and the turn ended during the store
  // step, so the slot stays and the card shows "not available" instead of
  // vanishing from history (#3442).
  return ordered.filter((activity) => {
    const hasUrl = nonEmptyString(activity.details?.image_url)
    const hasFile = nonEmptyString(activity.details?.session_file)
    return isImageFinishedFrame(activity.details) || hasUrl || hasFile
  })
}

/**
 * Name the stored file for an image entry that lost its final frame. The
 * image tool stores every image as `image_<job_id>.<ext>` and records that
 * name on the turn before the frame naming it goes out; when a durable turn
 * is cancelled during the store step, the frame is dropped but the name still
 * reaches the persisted message's `assistant_files` and is registered at end
 * of turn (#3442). Entries that already carry a name are left alone.
 */
export function resolveImageSessionFiles(
  activities: ActivityMessage[],
  assistantFiles: readonly string[]
): ActivityMessage[] {
  if (assistantFiles.length === 0) return activities
  return activities.map((activity) => {
    const details = activity.details
    const jobId = jobIdOf(activity)
    if (!jobId || !details || nonEmptyString(details.session_file)) return activity
    const prefix = `image_${jobId}.`
    const stored = assistantFiles.find(
      (name) =>
        name.startsWith(prefix) &&
        name === `${prefix}${imageFileExtension(name)}` &&
        isImageFileExtension(imageFileExtension(name))
    )
    return stored ? { ...activity, details: { ...details, session_file: stored } } : activity
  })
}
